import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';
import { alpha, useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import React, { type JSX } from 'react';
import AiLogoIcon from 'assets/icons/quicken-ai/ai-logo-icon.inline.svg';
import { AI_LOGO_SIMPLE_LAUNCHER_GLYPH_SIZE, AI_LOGO_SIMPLE_MASK } from 'assets/icons/quicken-ai/aiLogoSimple';
import MessageStarIcon from 'assets/icons/quicken-ai/message-star.inline.svg';
import PenStarIcon from 'assets/icons/quicken-ai/pen-star.inline.svg';
import SearchStarIcon from 'assets/icons/quicken-ai/search-star.inline.svg';
import SummarizeStarIcon from 'assets/icons/quicken-ai/summarize-star.inline.svg';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const StyledAIIcon = styled(Box)<{ size?: 'small' | 'medium' | 'large' }>(({ theme, size = 'medium' }) => {
  const sizeMap = {
    small: {
      container: theme.spacing(0.75),
      icon: theme.spacing(2.25),
      paddingY: theme.spacing(1),
      paddingX: theme.spacing(0.75),
    },
    medium: {
      container: theme.spacing(1),
      icon: theme.spacing(2.5),
      paddingY: theme.spacing(1.25),
      paddingX: theme.spacing(1),
    },
    large: {
      container: theme.spacing(1.25),
      icon: theme.spacing(2.75),
      paddingY: theme.spacing(1.5),
      paddingX: theme.spacing(1.25),
    },
  };

  const currentSize = sizeMap[size as keyof typeof sizeMap];

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingX: currentSize.paddingY,
    paddingY: currentSize.paddingX,

    color: theme.palette.ai.gradient,
    '& > svg, & > [data-ai-logo-simple]': {
      width: currentSize.icon,
      height: currentSize.icon,
    },
  };
});

const buttonSizeMap = {
  small: { padding: 0.75 },
  medium: { padding: 1 },
  large: { padding: 1.25 },
} as const;

const StyledButton = styled(Button)<{ size?: 'small' | 'medium' | 'large' }>(({ theme, size = 'medium' }) => {
  const { padding: p } = buttonSizeMap[size as keyof typeof buttonSizeMap];
  return {
    '&.icon-only': {
      padding: theme.spacing(p),
      minWidth: 'auto',
      width: 'auto',
    },
  };
});

// ============================================================================
// TYPES
// ============================================================================

/** Built-in icon names from assets/icons/quicken-ai. Default is 'summarize-star'. */
export type AILauncherIconName =
  | 'summarize-star'
  | 'pen-star'
  | 'message-star'
  | 'search-star'
  | 'ai-logo-simple'
  | 'ai-logo-icon';

/**
 * `ai-logo-simple` with theme AI gradient fill (CSS mask) and glyph scale matching
 * the former SVG viewBox inset. Gradient can't be applied via `currentColor`.
 */
const AiLogoSimpleIconPadded = (_props: React.SVGProps<SVGSVGElement>): JSX.Element => {
  const theme = useTheme();
  return (
    <Box
      component='span'
      aria-hidden
      data-ai-logo-simple
      sx={{
        display: 'block',
        background: theme.palette.ai.gradient,
        mask: `${AI_LOGO_SIMPLE_MASK} center / ${AI_LOGO_SIMPLE_LAUNCHER_GLYPH_SIZE} no-repeat`,
        WebkitMask: `${AI_LOGO_SIMPLE_MASK} center / ${AI_LOGO_SIMPLE_LAUNCHER_GLYPH_SIZE} no-repeat`,
      }}
    />
  );
};

/** Map of built-in icon names to icon components. Use for pickers or when passing icon by name. */
export const AILAUNCHER_ICONS: Record<AILauncherIconName, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  'summarize-star': SummarizeStarIcon,
  'pen-star': PenStarIcon,
  'message-star': MessageStarIcon,
  'search-star': SearchStarIcon,
  'ai-logo-simple': AiLogoSimpleIconPadded,
  'ai-logo-icon': AiLogoIcon,
};

export interface AILauncherProps {
  /** The label text to display */
  label?: string;
  /** The variant of the button */
  variant?: 'icon' | 'full';
  /** The size of the button */
  size?: 'small' | 'medium' | 'large';
  /** The color of the button */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  /**
   * Icon to show. Built-in names from quicken-ai folder, or any custom React node.
   * Default: 'summarize-star'.
   */
  icon?: AILauncherIconName | React.ReactNode;
  /**
   * When to run the border rotation animation. No default – omit for no border animation.
   * - 'onLoad': rotate once when the component mounts
   * - 'onHover': rotate once after hover delay
   * - 'both': rotate on load and again on hover
   */
  borderAnimation?: 'onLoad' | 'onHover' | 'both';
  /**
   * Scale the icon in step with the hover border sweep. Starts when that sweep
   * starts and uses the same 6s duration. Requires `borderAnimation` so the
   * sweep keyframes exist.
   */
  pulseIcon?: boolean;
  /** Show an arrow on the icon-variant tooltip. Off by default. */
  tooltipArrow?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Whether to render as an anchor element for popovers */
  asAnchor?: boolean;
  /** Anchor element ID for popover positioning */
  anchorId?: string;
  /** Additional CSS classes */
  className?: string;
  /** Additional props to pass to the Button component */
  [key: string]: unknown;
}

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

const useAILauncher = (props: AILauncherProps) => {
  const {
    label = 'Summarize',
    variant = 'full',
    size = 'medium',
    color = 'primary',
    icon = 'summarize-star',
    borderAnimation,
    pulseIcon = false,
    tooltipArrow = false,
    disabled = false,
    onClick,
    asAnchor = false,
    anchorId,
    className,
    ...otherProps
  } = props;

  const theme = useTheme();

  // Create reusable AI icon: built-in name, custom ReactNode, or default summarize-star
  const aiIcon = React.useMemo(() => {
    const IconComponent =
      typeof icon === 'string' ? (AILAUNCHER_ICONS[icon as AILauncherIconName] ?? SummarizeStarIcon) : null;
    const content =
      IconComponent != null ? <IconComponent /> : React.isValidElement(icon) ? icon : <SummarizeStarIcon />;
    return <StyledAIIcon size={size}>{content}</StyledAIIcon>;
  }, [size, icon]);

  // Prepare button props
  const { variant: customVariant, ...restProps } = otherProps;
  const buttonProps = {
    variant: 'tonal' as const,
    size,
    color,
    disabled,
    onClick,
    className: `${className || ''} ${variant === 'icon' ? 'icon-only' : ''}`.trim(),
    sx: {},
    ...(asAnchor && anchorId && { id: anchorId }),
    ...restProps,
  };

  return {
    label,
    variant,
    aiIcon,
    buttonProps,
    theme,
    borderAnimation,
    pulseIcon,
    tooltipArrow,
  };
};

// ============================================================================
// CONTENT RENDERERS
// ============================================================================

const IconContent = ({ aiIcon }: { aiIcon: React.ReactNode }): JSX.Element => <>{aiIcon}</>;

type FullContentProps = {
  aiIcon: React.ReactNode;
  label?: string;
  theme: Theme;
};

const FullContent = ({ aiIcon, label, theme }: FullContentProps): JSX.Element => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
    {aiIcon}
    {label && <span>{label}</span>}
  </Box>
);

const BORDER_WIDTH = 2;
const BORDER_WIDTH_ICON = 1;

type AIWrapperProps = {
  borderAnimation?: 'onLoad' | 'onHover' | 'both';
  variant?: 'icon' | 'full';
};

const AIWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'borderAnimation' && prop !== 'variant',
})<AIWrapperProps>(({ theme, borderAnimation, variant = 'full' }) => {
  const gradientColors = [
    alpha(theme.palette.data.sequence[0], 0),
    theme.palette.data.sequence[0],
    theme.palette.data.sequence[1],
    theme.palette.data.sequence[5],
    theme.palette.data.sequence[0],
    alpha(theme.palette.data.sequence[0], 0),
  ].join(', ');
  const animateOnLoad = borderAnimation === 'onLoad' || borderAnimation === 'both';
  const animateOnHover = borderAnimation === 'onHover' || borderAnimation === 'both';
  const showBorder = animateOnLoad || animateOnHover;
  const borderWidth = variant === 'icon' ? BORDER_WIDTH_ICON : BORDER_WIDTH;
  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    padding: showBorder ? borderWidth : 0,
    overflow: 'hidden',
    transition: 'box-shadow 0.3s ease, transform 0.2s ease',
    ...(showBorder
      ? {
          '@keyframes aiBorderRotate': {
            '0%': { transform: 'rotate(0deg) translateZ(0)', opacity: 0 },
            '30%': { transform: 'rotate(30deg) translateZ(0)', opacity: 1 },
            '70%': { transform: 'rotate(270deg) translateZ(0)', opacity: 1 },
            '100%': { transform: 'rotate(360deg) translateZ(0)', opacity: 0 },
          },
          '@keyframes aiBorderRotateHover': {
            '0%': { transform: 'rotate(0deg) translateZ(0)', opacity: 0 },
            '30%': { transform: 'rotate(30deg) translateZ(0)', opacity: 1 },
            '70%': { transform: 'rotate(270deg) translateZ(0)', opacity: 1 },
            '100%': { transform: 'rotate(360deg) translateZ(0)', opacity: 0 },
          },
          '@keyframes aiStarPulse': {
            '0%': { transform: 'scale(1)' },
            '16%': { transform: 'scale(1.18)' },
            '34%': { transform: 'scale(1)' },
            '54%': { transform: 'scale(1.12)' },
            '74%': { transform: 'scale(1)' },
            '100%': { transform: 'scale(1)' },
          },
          '&.pulse-icon.run-hover-animation [data-ai-logo-simple]': {
            transformOrigin: 'center',
            animation: 'aiStarPulse 6s ease-in-out 1 forwards',
          },
        }
      : {}),
    '&::before': {
      content: animateOnLoad ? '""' : 'none',
      position: 'absolute',
      inset: 0,
      background: animateOnLoad ? `conic-gradient(from 0deg, ${gradientColors})` : 'none',
      filter: theme.palette.blur.soft,
      borderRadius: '8px',
      zIndex: 0,
      ...(animateOnLoad
        ? {
            animation: 'aiBorderRotate 6s linear 1 forwards',
            willChange: 'transform',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
          }
        : {}),
    },
    '&::after': {
      content: showBorder ? '""' : 'none',
      position: 'absolute',
      inset: 0,
      background: showBorder ? `conic-gradient(from 0deg, ${gradientColors})` : 'none',
      filter: theme.palette.blur.soft,
      borderRadius: '8px',
      zIndex: 0,
      opacity: 0,
      pointerEvents: 'none',
    },
    ...(animateOnHover
      ? {
          '&.run-hover-animation::after': {
            animation: 'aiBorderRotateHover 6s linear 1 forwards',
            willChange: 'transform',
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
          },
        }
      : {}),
    '&:hover': {
      transform: 'translateY(-2px)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&::before': {
        animation: 'none',
        opacity: 0,
      },
      '&.run-hover-animation::after': {
        animation: 'none',
      },
      '&.pulse-icon.run-hover-animation [data-ai-logo-simple]': {
        animation: 'none',
      },
      '&:hover': {
        transform: 'none',
      },
    },
  };
});

const AIWrapperInner = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  backgroundColor: alpha(theme.palette.backdrop.light, 0.5),
  backdropFilter: theme.palette.blur.soft,
}));

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const HOVER_DELAY_MS = 350;

const AILauncher = (props: AILauncherProps): JSX.Element | null => {
  const [AIEnabled] = usePreferenceV2('aiInsightsOptIn');
  const { label, variant, aiIcon, buttonProps, theme, borderAnimation, pulseIcon, tooltipArrow } = useAILauncher(props);
  const [runHoverAnimation, setRunHoverAnimation] = React.useState(false);
  const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const animateOnHover = borderAnimation === 'onHover' || borderAnimation === 'both';

  const clearHoverTimer = React.useCallback(() => {
    if (hoverTimerRef.current !== null) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const handleMouseEnter = React.useCallback(() => {
    clearHoverTimer();
    if (!animateOnHover) return;
    hoverTimerRef.current = setTimeout(() => {
      hoverTimerRef.current = null;
      setRunHoverAnimation(true);
    }, HOVER_DELAY_MS);
  }, [clearHoverTimer, animateOnHover]);

  const handleMouseLeave = React.useCallback(() => {
    clearHoverTimer();
  }, [clearHoverTimer]);

  const handleAnimationEnd = React.useCallback((e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'aiBorderRotateHover') {
      setRunHoverAnimation(false);
    }
  }, []);

  React.useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  // Render button content based on variant
  const renderButtonContent = () => {
    switch (variant) {
      case 'icon':
        return <IconContent aiIcon={aiIcon} />;
      case 'full':
      default:
        return <FullContent aiIcon={aiIcon} label={label} theme={theme} />;
    }
  };

  const wrappedButtonProps = {
    ...buttonProps,
    disableElevation: true,
    sx: {
      border: `1px solid ${theme.palette.border.light}`,
      borderRadius: '8px',
      backgroundColor: theme.palette.backdrop.light,
      backdropFilter: theme.palette.blur.soft,
      '&:hover': {
        border: `1px solid transparent`,
        backgroundColor: theme.palette.backdrop.light,
        boxShadow: theme.shadows[1],
        color: theme.palette.primary.main,
      },
    },
  };

  const buttonContent = (
    <AIWrapper
      borderAnimation={borderAnimation}
      variant={variant}
      className={
        [pulseIcon ? 'pulse-icon' : '', runHoverAnimation ? 'run-hover-animation' : ''].filter(Boolean).join(' ') ||
        undefined
      }
      onAnimationEnd={handleAnimationEnd}
    >
      <AIWrapperInner>
        <StyledButton {...wrappedButtonProps}>{renderButtonContent()}</StyledButton>
      </AIWrapperInner>
    </AIWrapper>
  );

  // When hover animation is used, wrap in a dedicated element that captures hover so it works
  // even when AILauncher is wrapped by Tooltip or rendered inside grid cells.
  const wrappedButton = animateOnHover ? (
    <Box
      component='span'
      sx={{ display: 'inline-flex' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {buttonContent}
    </Box>
  ) : (
    buttonContent
  );

  if (!AIEnabled) return null;

  if (variant === 'icon' && label) {
    return (
      <Tooltip title={label} arrow={tooltipArrow} disableInteractive={animateOnHover}>
        {wrappedButton}
      </Tooltip>
    );
  }

  return wrappedButton;
};

export default AILauncher;
